/**
 * The printing queue.
 *
 * A table with the things the print room actually needs: how many copies, for
 * which paper, by when. Before this existed the queue showed a title and a file,
 * so those questions were settled by walking down the corridor - the manual step
 * this product removes everywhere else.
 *
 * Downloads serve the *frozen* artifact, never a fresh render. Re-rendering here
 * would let a template edit between approval and printing change what actually
 * comes out of the printer (DR-003).
 */

import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api, saveBlob } from '../../lib/api/client'
import type { QueueItem } from '../../lib/api/client'
import { Button, Dialog, EmptyState, HeaderMetrics, PageHeader, Pill, Spinner } from '../../components/ui'
import { Icon } from '../../components/icons'
import { STATUS_TONE } from '../../components/statusTone'
import { toast } from '../../components/toast'
import { when } from './format'
import './queues.css'

/** What the user has asked to do, held until they confirm it. */
type PendingPrint = { action: 'mark_printed' | 'send_to_print'; ids: string[] }

export function Printing() {
  const queryClient = useQueryClient()
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [pending, setPending] = useState<PendingPrint | null>(null)

  const queue = useQuery({ queryKey: ['queue', 'printing'], queryFn: api.printQueue })

  const refresh = () => {
    setSelected(new Set())
    setPending(null)
    queryClient.invalidateQueries({ queryKey: ['queue', 'printing'] })
  }

  /**
   * Mark papers printed.
   *
   * The queue holds two states, and this is the bug that made the button do
   * nothing: `mark_printed` is only legal from "With printing", while a paper the
   * reviewer has just approved is still "Approved" and needs `send_to_print`
   * first. So each paper takes whichever steps its own state requires.
   *
   * Both transitions are recorded rather than one being skipped - the paper really
   * did go to the print room and really was printed, and the audit trail should
   * say so.
   *
   * Sequential rather than parallel: a partial failure should stop rather than
   * leave the queue in a state nobody can explain.
   */
  const markPrinted = useMutation({
    mutationFn: async (ids: string[]) => {
      const byId = new Map(rows.map((row) => [row.id, row.status]))
      for (const id of ids) {
        if (byId.get(id) === 'APPROVED') {
          await api.perform(id, { action: 'send_to_print', reviewer_id: null, comment: null })
        }
        await api.perform(id, { action: 'mark_printed', reviewer_id: null, comment: null })
      }
    },
    onSuccess: refresh,
    onError: (error: Error) => toast(error.message, 'error'),
  })

  const sendToPrint = useMutation({
    mutationFn: (id: string) =>
      api.perform(id, { action: 'send_to_print', reviewer_id: null, comment: null }),
    onSuccess: refresh,
    onError: (error: Error) => toast(error.message, 'error'),
  })

  const batch = useMutation({
    mutationFn: (ids: string[]) => api.batchDownload(ids),
    onSuccess: (blob) => saveBlob(blob, 'approved-papers.zip'),
    onError: (error: Error) => toast(error.message, 'error'),
  })

  if (queue.isLoading) return <Spinner label="Loading the printing queue" />

  const rows = queue.data ?? []
  const chosen = [...selected]
  const allChosen = rows.length > 0 && chosen.length === rows.length
  const totalCopies = rows.reduce((sum, row) => sum + (row.print_run.copies ?? 0), 0)
  const missingCopies = rows.filter((row) => row.print_run.copies == null).length

  const toggle = (id: string) => {
    const next = new Set(selected)
    if (next.has(id)) next.delete(id)
    else next.add(id)
    setSelected(next)
  }

  return (
    <div className="tc-printing">
      <PageHeader
        eyebrow="Print room"
        title="Printing"
        icon={<Icon.printing size={20} />}
        description="Download approved files, track copy requirements, and clear completed jobs."
        actions={
          <HeaderMetrics
            label="Printing queue summary"
            items={[
              { value: rows.length, label: 'Waiting' },
              { value: totalCopies || '—', label: 'Copies listed' },
              { value: missingCopies, label: 'Missing copy count', tone: missingCopies ? 'warning' : 'default' },
            ]}
          />
        }
      />

      {rows.length === 0 ? (
        <EmptyState
          icon={<Icon.printing size={20} />}
          title="Nothing waiting"
          message="Approved papers appear here. What you download is exactly the file that was approved."
        />
      ) : (
        <section className="tc-printing-panel" aria-label="Papers waiting to print">
          <div className="tc-printing-actions">
            <div>
              <strong>{chosen.length ? `${chosen.length} selected` : `${rows.length} ${rows.length === 1 ? 'paper' : 'papers'}`}</strong>
              <span>{chosen.length ? 'Batch actions apply only to selected papers.' : 'Select papers to use batch actions.'}</span>
            </div>
            {chosen.length > 0 && (
              <div className="tc-printing-batch">
                <Button
                  size="sm"
                  icon={<Icon.download size={15} />}
                  disabled={batch.isPending}
                  onClick={() => batch.mutate(chosen)}
                >
                  Download zip
                </Button>
                <Button
                  size="sm"
                  variant="primary"
                  disabled={markPrinted.isPending}
                  onClick={() => setPending({ action: 'mark_printed', ids: chosen })}
                >
                  Mark printed
                </Button>
              </div>
            )}
          </div>
          <div className="tc-printing-table-wrap">
          <table className="tc-table tc-printing-table">
          <thead>
            <tr>
              <th style={{ width: 32 }}>
                {/* `indeterminate` is not an attribute, so it has to be set on
                    the node. A part-selected queue otherwise showed an empty box
                    that looked like "nothing is selected". */}
                <input
                  type="checkbox"
                  aria-label="Select every paper"
                  ref={(node) => {
                    if (node) node.indeterminate = chosen.length > 0 && !allChosen
                  }}
                  checked={allChosen}
                  onChange={() => setSelected(allChosen ? new Set() : new Set(rows.map((r) => r.id)))}
                />
              </th>
              <th>Paper</th>
              <th style={{ width: 150 }}>From</th>
              <th style={{ width: 80 }}>Copies</th>
              <th style={{ width: 120 }}>Needed by</th>
              <th>Notes</th>
              <th style={{ width: 130 }}>Status</th>
              <th style={{ width: 190 }}>
                <span className="tc-sr-only">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <tr key={row.id}>
                <td>
                  <input
                    type="checkbox"
                    aria-label={`Select ${row.title}`}
                    checked={selected.has(row.id)}
                    onChange={() => toggle(row.id)}
                  />
                </td>
                <td data-label="Paper"><strong className="tc-printing-title">{row.title}</strong></td>
                <td data-label="From">{row.author}</td>
                <td data-label="Copies">
                  {row.print_run.copies ?? <Pill tone="warning">Needed</Pill>}
                </td>
                <td data-label="Needed by">{row.print_run.needed_by ?? <span className="tc-field-hint">No deadline</span>}</td>
                <td data-label="Notes" className="tc-notes-cell" title={row.print_run.notes ?? ''}>
                  {row.print_run.notes
                    ? <span>{row.print_run.notes}</span>
                    : <span className="tc-field-hint">No notes</span>}
                </td>
                <td data-label="Status">
                  <div className="tc-printing-status">
                    <Pill tone={STATUS_TONE[row.status] ?? 'muted'}>{row.status_label}</Pill>
                    <span className="tc-field-hint">
                    {when(row.submitted_at)}
                    </span>
                  </div>
                </td>
                <td data-label="Actions">
                  <div className="tc-queue-row-actions">
                    <Button
                      size="sm"
                      icon={<Icon.download size={14} />}
                      onClick={() =>
                        api
                          .approvedFile(row.id)
                          .then((blob) => saveBlob(blob, `${row.title}.docx`))
                          .catch((error: Error) => toast(error.message, 'error'))
                      }
                    >
                      Download
                    </Button>
                    {/* The action a paper can actually take, from where it is. */}
                    {row.status === 'APPROVED' ? (
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => setPending({ action: 'send_to_print', ids: [row.id] })}
                      >
                        Send to print
                      </Button>
                    ) : (
                      <Button
                        size="sm"
                        variant="primary"
                        onClick={() => setPending({ action: 'mark_printed', ids: [row.id] })}
                      >
                        Printed
                      </Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
          </div>
        </section>
      )}

      <PrintConfirm
        pending={pending}
        rows={rows}
        busy={markPrinted.isPending || sendToPrint.isPending}
        onCancel={() => setPending(null)}
        onConfirm={() => {
          if (!pending) return
          if (pending.action === 'send_to_print') sendToPrint.mutate(pending.ids[0])
          else markPrinted.mutate(pending.ids)
        }}
      />
    </div>
  )
}

/**
 * Asks before a status change that cannot be taken back.
 *
 * "Mark printed" closes a job: the paper leaves the queue and the print room has
 * no way to put it back. It had no confirmation at all, while archiving a
 * *draft* - far less destructive - already asked for one. This also puts the
 * missing copy count in front of the person about to act on it. The queue header
 * reported that number and then let the job through anyway, so the one fact the
 * print room actually needs was shown everywhere except where it mattered.
 */
function PrintConfirm({
  pending,
  rows,
  busy,
  onCancel,
  onConfirm,
}: {
  pending: PendingPrint | null
  rows: QueueItem[]
  busy: boolean
  onCancel: () => void
  onConfirm: () => void
}) {
  const chosen = rows.filter((row) => pending?.ids.includes(row.id))
  const withoutCopies = chosen.filter((row) => row.print_run.copies == null)
  const printing = pending?.action === 'mark_printed'
  const count = pending?.ids.length ?? 0

  return (
    <Dialog
      open={pending !== null}
      title={
        printing
          ? `Mark ${count === 1 ? 'this paper' : `${count} papers`} printed?`
          : 'Send this paper to print?'
      }
      onClose={() => !busy && onCancel()}
      footer={
        <>
          <Button disabled={busy} onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" disabled={busy} onClick={onConfirm}>
            {busy ? 'Working…' : printing ? 'Yes, mark printed' : 'Yes, send to print'}
          </Button>
        </>
      }
    >
      <p className="tc-print-confirm-text">
        {printing
          ? 'The paper leaves the printing queue and is recorded as printed. This cannot be undone from here.'
          : 'The paper moves to the print room and is recorded as with printing.'}
      </p>

      {withoutCopies.length > 0 && (
        <div className="tc-print-confirm-warning" role="note">
          <Icon.warning size={16} />
          <div>
            <strong>
              {withoutCopies.length === 1
                ? 'No copy count on this paper.'
                : `No copy count on ${withoutCopies.length} of these papers.`}
            </strong>
            <p>
              The teacher has not said how many to run
              {withoutCopies.length <= 3
                ? `: ${withoutCopies.map((row) => row.title).join(', ')}.`
                : '.'}{' '}
              You can still go ahead, but the print room will have to ask.
            </p>
          </div>
        </div>
      )}

      {chosen.length > 1 && (
        <ul className="tc-print-confirm-list">
          <li>
            <span>Paper</span>
            <span>Copies</span>
          </li>
          {chosen.map((row) => (
            <li key={row.id}>
              <span>{row.title}</span>
              <span>{row.print_run.copies ?? '—'}</span>
            </li>
          ))}
        </ul>
      )}
    </Dialog>
  )
}
